// LoginPage.js - Authentication Page

import React, { useState } from 'react';
import styled from 'styled-components';
import api from '../../utils/api';
import useStore from '../../store/useStore';
import THEME from '../../styles/theme';

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { setUser } = useStore();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      let response;
      if (isLogin) {
        response = await api.login(username, password);
      } else {
        response = await api.register(username, password);
      }

      setUser(response.user);
    } catch (err) {
      setError(err.message || 'Authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container>
      <LoginBox>
        <Title>GChain</Title>
        <Subtitle>RF Link Budget & Modeling Tool</Subtitle>

        <Form onSubmit={handleSubmit}>
          <Input
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />

          <Input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <SubmitButton type="submit" disabled={isLoading}>
            {isLoading ? 'Please wait...' : (isLogin ? 'Login' : 'Register')}
          </SubmitButton>
        </Form>

        <ToggleText>
          {isLogin ? "Don't have an account? " : "Already have an account? "}
          <ToggleLink onClick={() => {
            setIsLogin(!isLogin);
            setError('');
          }}>
            {isLogin ? 'Register' : 'Login'}
          </ToggleLink>
        </ToggleText>
      </LoginBox>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 100vh;
  background-color: ${THEME.canvasBackground};
`;

const LoginBox = styled.div`
  background: ${THEME.panelBackground};
  border: 1px solid #404040;
  border-radius: ${THEME.borderRadiusLarge};
  padding: ${THEME.spacing.xxl};
  width: 400px;
  box-shadow: ${THEME.shadowLarge};
`;

const Title = styled.h1`
  color: ${THEME.accentPrimary};
  font-size: 32px;
  text-align: center;
  margin-bottom: ${THEME.spacing.sm};
`;

const Subtitle = styled.p`
  color: ${THEME.textSecondary};
  font-size: ${THEME.fontSize.md};
  text-align: center;
  margin-bottom: ${THEME.spacing.xxl};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${THEME.spacing.lg};
`;

const Input = styled.input`
  background: ${THEME.inputBackground};
  border: 1px solid ${THEME.inputBorder};
  border-radius: ${THEME.borderRadiusSmall};
  padding: ${THEME.spacing.md};
  color: ${THEME.inputText};
  font-size: ${THEME.fontSize.md};
  transition: border-color ${THEME.transitionFast};

  &:focus {
    border-color: ${THEME.inputBorderFocus};
    outline: none;
  }

  &::placeholder {
    color: ${THEME.textMuted};
  }
`;

const SubmitButton = styled.button`
  background: ${THEME.buttonPrimary};
  color: ${THEME.textPrimary};
  padding: ${THEME.spacing.md};
  border-radius: ${THEME.borderRadiusSmall};
  font-size: ${THEME.fontSize.md};
  font-weight: 600;
  transition: background ${THEME.transitionFast};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${THEME.buttonHover};
  }

  &:disabled {
    background: ${THEME.buttonDisabled};
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: ${THEME.error};
  font-size: ${THEME.fontSize.sm};
  text-align: center;
  padding: ${THEME.spacing.sm};
  background: rgba(255, 165, 0, 0.1);
  border-radius: ${THEME.borderRadiusSmall};
`;

const ToggleText = styled.p`
  color: ${THEME.textSecondary};
  font-size: ${THEME.fontSize.sm};
  text-align: center;
  margin-top: ${THEME.spacing.lg};
`;

const ToggleLink = styled.span`
  color: ${THEME.accentPrimary};
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: ${THEME.accentHover};
  }
`;

export default LoginPage;
